import { computed, reactive } from 'vue';

export const MAX_WHATSAPP_FORWARD_SELECTION = 30;

const FORWARD_COPY = {
  en: {
    'menu.select': 'Select messages to forward',
    'menu.forward': 'Forward via WhatsApp',
    'dialog.title.single': 'Forward message',
    'dialog.title.multiple': 'Forward messages',
    'dialog.description':
      "WhatsApp's native forwarding is used, so the recipient can see it was forwarded.",
    'dialog.forwarding': 'Forwarding:',
    'dialog.messages': '{count} messages',
    'dialog.call': 'Call record',
    'dialog.vcard': 'Contact card',
    'dialog.media': 'Media message',
    'dialog.search.aria': 'Search WhatsApp conversations',
    'dialog.search.placeholder': 'Search a person or number',
    'dialog.searching': 'Searching conversations…',
    'dialog.search.hint': 'Enter at least two characters to find another conversation in this WhatsApp inbox.',
    'dialog.search.empty': 'No suitable conversation was found in this WhatsApp inbox.',
    'dialog.conversation': 'Conversation #{id}',
    'dialog.success.single': 'Message forwarded through WhatsApp.',
    'dialog.success.multiple': '{count} messages forwarded through WhatsApp.',
    'dialog.error': 'The message could not be forwarded through WhatsApp. Please try again.',
    'selection.add': 'Add to messages to forward',
    'selection.remove': 'Remove from messages to forward',
    'selection.limit': 'You can forward up to {count} messages at once.',
    'selection.none': 'Select at least one message to forward.',
    'selection.selected': '{count} messages selected',
    'selection.cancel': 'Cancel',
    'selection.forward': 'Forward',
    'error.chat_unavailable':
      'WhatsApp has not loaded the target conversation yet. Open that chat in WhatsApp, then try again.',
    'error.message_unavailable':
      'WhatsApp no longer has this source message available to forward.',
    'error.forward_rejected': 'WhatsApp rejected this forwarding request. Please try again.',
  },
  tr: {
    'menu.select': 'Mesaj seçerek ilet',
    'menu.forward': 'WhatsApp’a ilet',
    'dialog.title.single': 'Mesajı ilet',
    'dialog.title.multiple': 'Mesajları ilet',
    'dialog.description':
      'WhatsApp’ın kendi iletme özelliği kullanılır; alıcıya iletildi bilgisi de gider.',
    'dialog.forwarding': 'İletilecek:',
    'dialog.messages': '{count} mesaj',
    'dialog.call': 'Arama kaydı',
    'dialog.vcard': 'Kişi kartı',
    'dialog.media': 'Medya mesajı',
    'dialog.search.aria': 'WhatsApp konuşması ara',
    'dialog.search.placeholder': 'Kişi veya numara ara',
    'dialog.searching': 'Konuşmalar aranıyor…',
    'dialog.search.hint': 'Aynı WhatsApp gelen kutusundaki bir konuşmayı bulmak için en az iki karakter yazın.',
    'dialog.search.empty': 'Bu WhatsApp gelen kutusunda uygun başka bir konuşma bulunamadı.',
    'dialog.conversation': 'Konuşma #{id}',
    'dialog.success.single': 'Mesaj WhatsApp üzerinden iletildi.',
    'dialog.success.multiple': '{count} mesaj WhatsApp üzerinden iletildi.',
    'dialog.error': 'Mesaj WhatsApp üzerinden iletilemedi. Lütfen tekrar deneyin.',
    'selection.add': 'İletilecek mesajlara ekle',
    'selection.remove': 'İletilecek mesaj seçiminden çıkar',
    'selection.limit': 'En fazla {count} mesaj birlikte iletilebilir.',
    'selection.none': 'İletmek için en az bir mesaj seçin.',
    'selection.selected': '{count} mesaj seçildi',
    'selection.cancel': 'Vazgeç',
    'selection.forward': 'İlet',
    'error.chat_unavailable':
      'WhatsApp hedef sohbetini henüz yüklemedi. WhatsApp’ta o sohbeti açıp tekrar deneyin.',
    'error.message_unavailable':
      'WhatsApp bu kaynak mesajı artık iletmek için bulamıyor.',
    'error.forward_rejected': 'WhatsApp bu iletme isteğini reddetti. Lütfen tekrar deneyin.',
  },
};

export function seamChatForwardText(locale, key, values = {}) {
  const language = String(locale || '').toLowerCase().startsWith('tr')
    ? 'tr'
    : 'en';
  const template = FORWARD_COPY[language][key] || FORWARD_COPY.en[key] || key;
  return template.replace(/\{(\w+)\}/g, (_match, name) => values[name] ?? `{${name}}`);
}

export const whatsappForwardSelection = reactive({
  conversationId: null,
  sourceInboxId: null,
  anchorMessageId: null,
  messages: [],
});

export const isWhatsAppForwardSelectionActive = computed(
  () => whatsappForwardSelection.conversationId !== null
);

function normalizeMessage(message) {
  return {
    id: Number(message.id),
    conversation_id: Number(message.conversation_id ?? message.conversationId),
    content: message.content || '',
    content_attributes:
      message.content_attributes ?? message.contentAttributes ?? {},
  };
}

function contains(messageId) {
  return whatsappForwardSelection.messages.some(
    message => Number(message.id) === Number(messageId)
  );
}

export function startWhatsAppForwardSelection(message, sourceInboxId) {
  const selectedMessage = normalizeMessage(message);
  whatsappForwardSelection.conversationId = selectedMessage.conversation_id;
  whatsappForwardSelection.sourceInboxId = sourceInboxId;
  whatsappForwardSelection.anchorMessageId = selectedMessage.id;
  whatsappForwardSelection.messages = [selectedMessage];
}

export function toggleWhatsAppForwardSelection(message) {
  const selectedMessage = normalizeMessage(message);
  if (
    Number(whatsappForwardSelection.conversationId) !==
    selectedMessage.conversation_id
  ) {
    return { changed: false, reason: 'conversation' };
  }

  if (contains(selectedMessage.id)) {
    whatsappForwardSelection.messages = whatsappForwardSelection.messages.filter(
      item => Number(item.id) !== selectedMessage.id
    );
    return { changed: true, selected: false };
  }

  if (
    whatsappForwardSelection.messages.length >= MAX_WHATSAPP_FORWARD_SELECTION
  ) {
    return { changed: false, reason: 'limit' };
  }

  whatsappForwardSelection.messages.push(selectedMessage);
  return { changed: true, selected: true };
}

export function isWhatsAppForwardMessageSelected(messageId) {
  return contains(messageId);
}

export function clearWhatsAppForwardSelection() {
  whatsappForwardSelection.conversationId = null;
  whatsappForwardSelection.sourceInboxId = null;
  whatsappForwardSelection.anchorMessageId = null;
  whatsappForwardSelection.messages = [];
}
